/* @flow */
import { Observable } from 'rxjs';
import { map, pipe } from 'ramda';
import { combineEpics } from 'redux-observable';
import { getCsrfToken } from 'trustar/utils';
import { TRUSTAR_API_HOST } from './constants';
import {IS_LOGGED_IN_LOCALSTORAGE_KEY, LOGIN_ENDPOINT_AUTHPATH} from '../../../src/app/modules/app/constants';

/*
 * Handler for errors thrown from individual module epics.
 */
const handleError = (error: Object, name: string): void => {
  const message = `Uncaught error in epic: ${name}`;
  if (process.env.NODE_ENV === 'production') {
    console.warn(message);
    console.error(error);
    // TODO_LOG Raven.captureException(
    // TODO_LOG   error,
    // TODO_LOG   { extra: { name } }
    // TODO_LOG );
  } else {
    console.warn(message);
    console.error(error);
  }
};

/*
 * Error wrapper for individual module epics. This handles uncaught errors
 * and returns the original stream, so the error does not terminate the epic.
 */
const safelyWrapEpic = ({ epic, name }: Object): Function => (...args) => {
  return epic(...args)
    .catch((error, source) => {
      handleError(error, name);
      return source;
    });
};

/*
 * Creates the root epic, given an array of epics, wrapping
 * each of the provided epics in a safe error catching wrapper.
 *
 * The purpose for this is that an uncaught error from an epic
 * will bubble up to the main epic and cause the entire stream
 * to terminate. This would render the app unusable, so we wrap
 * each individual epic in an error handler to prevent this from
 * happening. The wrapper handles the error and returns the original
 * stream, which revives the epic.
 *
 */
export const createRootEpic = (epics: Array<Object>) => {
  return pipe(
    map(safelyWrapEpic),
    epics => combineEpics(...epics),
  )(epics);
};

/**
 * Traditionally, if any of these statuses were returned from the server we
 * would log the user out.
 */
const logoutStatuses = new Set([401, 403, 502]);

const onRequestFailure = (error) => {
  const { status } = error;

  if (logoutStatuses.has(status)) {
    console.warn(`[ LOGOUT WARNING ]: Got ${status} from server.`);

    // Suspicion
    const userLoggedIn = !!getCsrfToken() && window.localStorage.getItem(IS_LOGGED_IN_LOCALSTORAGE_KEY) !== null;

    // Boot to login
    if (!userLoggedIn) {
      window.location.replace(LOGIN_ENDPOINT_AUTHPATH);
    }
  }
};

type RequestShape = {
  url?: string,
  body?: any,
  user?: string,
  async?: boolean,
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  headers?: Object,
  timeout?: number,
  password?: string,
  hasContent?: boolean,
  crossDomain?: boolean,
  withCredentials?: boolean,
  createXHR?: () => XMLHttpRequest,
  progressSubscriber?: Object,
  responseType?: 'json' | 'xml' | 'text',
};

export const createAjax = () => {

  const ajax = ({ headers = {}, ...rest }: RequestShape): Observable<Object> => {
    return Observable.ajax(Object.assign({}, {
      ...rest,
      headers: {
        'X-XSRF-TOKEN': getCsrfToken(),
        ...headers,
      },
    }));
  };

  /**
   * Manually pass in the ajax helpers. This makes testing epics easier because we
   * can very easily mock this interface for testing purposes by passing the
   * dependencies in to the epics directly during testing. See some of the epic
   * tests for examples.
   */
  ajax.get = (url, headers, config: RequestShape) => {
    const endpoint = TRUSTAR_API_HOST + url;
    return Observable.ajax({
      method: 'GET',
      url: endpoint,
      withCredentials: true,
      crossDomain: true,
      'X-XSRF-TOKEN': getCsrfToken(),
      headers: {
        'X-XSRF-TOKEN': getCsrfToken(),
        ...headers,
      },
      ...config,
    })
    .do(null, onRequestFailure);
  };

  ajax.getJSON = (url, headers, config: RequestShape) => {
    return Observable.ajax({
      method: 'GET',
      responseType: 'json',
      url: TRUSTAR_API_HOST + url,
      withCredentials: true,
      crossDomain: true,
      headers: {
        'X-XSRF-TOKEN': getCsrfToken(),
        ...headers,
      },
      ...config
    })
    .do(null, onRequestFailure);
  };

  ajax.delete = (url, headers, config: RequestShape) => {
    return Observable.ajax({
      method: 'DELETE',
      url: TRUSTAR_API_HOST + url,
      withCredentials: true,
      crossDomain: true,
      headers: {
        'X-XSRF-TOKEN': getCsrfToken(),
        ...headers
      },
      ...config
    })
    .do(null, onRequestFailure);
  };

  ajax.put = (url, headers, config: RequestShape) => {
    return Observable.ajax({
      url: TRUSTAR_API_HOST + url,
      method: 'PUT',
      withCredentials: true,
      crossDomain: true,
      headers: {
        'X-XSRF-TOKEN': getCsrfToken(),
        ...headers,
      },
      ...config
    })
    .do(null, onRequestFailure);
  };

  ajax.post = (url, body, headers, config: RequestShape) => {
    return Observable.ajax({
      method: 'POST',
      responseType: 'json',
      url: TRUSTAR_API_HOST + url,
      withCredentials: true,
      crossDomain: true,
      body: body,
      headers: {
        'X-XSRF-TOKEN': getCsrfToken(),
        ...headers,
      },
      ...config
    })
    .do(null, onRequestFailure);
  };


  ajax.putJSON = (url, body, headers, config: RequestShape) => {
    return Observable.ajax({
      url: TRUSTAR_API_HOST + url,
      method: 'PUT',
      withCredentials: true,
      crossDomain: true,
      body: body,
      headers: {
        'X-XSRF-TOKEN': getCsrfToken(),
        'Content-Type': 'application/json; charset=UTF-8',
        ...headers,
      },
      ...config
    })
    .do(null, onRequestFailure);
  };

  /**
   * Post some JSON to the server. This is entirely equivalent to
   * Observable.ajax.post except it defaults the content type to json instead of
   * form-urlencoded. Currently our back end will throw 500 if we do not do this.
   *
   * NOTE: This is the only method so far that is NOT part of the Observable.ajax
   * API. This is custom
   *
   * (url: string, body: ?Object, headers: ?Object) => Observable<*>;
   */
  ajax.postJSON = (url, body, headers, config: RequestShape) => {
    return Observable.ajax({
      method: 'POST',
      body: body,
      responseType: 'json',
      url: TRUSTAR_API_HOST + url,
      withCredentials: true,
      crossDomain: true,
      headers: {
        'X-XSRF-TOKEN': getCsrfToken(),
        'Content-Type': 'application/json; charset=UTF-8',
        ...headers,
      },
      ...config
    })
    .do(null, onRequestFailure);
  };

  /**
   * Like postJSON but uses form URL encoding. This is useful for interop with
   * Spring's @RequestParam decorator which does NOT like JSON even a little bit.
   */
  ajax.postForm = (url, body, headers) => {
    const formBody = new URLSearchParams();

    Object.keys(body).forEach(k => {
      formBody.set(k, body[k]);
    });

    return Observable.ajax.post(url, formBody.toString(), {
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
      'X-XSRF-TOKEN': getCsrfToken(),
      ...headers,
    });
  };

  return ajax;

};

/* NOTE: Without a state transformer the logger will always log our immutable
* state which is an instance of Immutable.Map. This will not prove very
* useful unless you install the formatter extension:
* https://github.com/mattzeunert/immutable-object-formatter-extension
*
* The reason for not providing a state transformer is that calling toJS on
* our entire app state for EVERY ACTION is actually a fairly intensive
* blocking computation on our single thread. This means that the dev
* environment will be considerably slower if you call toJS on state every
* action.
*/
export const LoggerConfig = {
  collapsed: true,
  timestamp: false,
  duration: true,
  diff: false,
  level: {
    prevState: false,
    action: () => 'log',
    nextState: () => 'log',
    error: () => 'log',
  },
  colors: {
    title: () => '#2A9DEF',
    prevState: () => '#FFE661',
    action: () => '#FB505D',
    nextState: () => '#2DD88A',
    error: () => '#FB1C2C',
  },
  predicate: (getState: Function, { type }: Object) => {

    /* NOTE: Filter certain Redux-Form acitons,
     * there are a lot and they slow down the UI
     *
     */

    if (
      type === '@@redux-form/CHANGE' ||
      type === '@@redux-form/REGISTER_FIELD' ||
      type === '@@redux-form/UNREGISTER_FIELD' 
    ) return false;
    return true;
  }
};



// WEBPACK FOOTER //
// ./web_modules/trustar/utils/redux-utils.js